import {ActionIcon} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {IconRefresh, IconSearch} from '@tabler/icons-react';
import React, {useCallback, useMemo, useRef} from 'react';
import {
  ReactFlow,
  type Node,
  type NodeTypes,
  type EdgeTypes,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type {NexusGraphResponse} from 'src/api/ProactApiClient';
import { NexusVisualState} from 'src/api/ProactApiClient';
import {EntityNodeCard} from './NexusGraphNode';
import {PhaseJunctionNode} from './PhaseJunctionNode';
import {HorizonNode} from './HorizonNode';
import {NexusFlowEdge} from './NexusGraphEdge';
import {StateLegend} from './NexusGraphLegend';
import {graphToFlow} from './nexusGraphLayout';
import type {NexusFlowNodeData} from './nexusGraphTypes';
import './NexusGraph.css';

interface NexusGraphProps {
  data: NexusGraphResponse | null;
  isLoading?: boolean;
  /** Use reduced canvas height for detail panel embedding */
  compact?: boolean;
  /** Called when the user clicks the refresh button */
  onRefresh?: () => void;
}

// --- Node / Edge Type Registration ---

const nodeTypes: NodeTypes = {
  nexusNode: EntityNodeCard as unknown as NodeTypes[string],
  phaseJunctionNode: PhaseJunctionNode as unknown as NodeTypes[string],
  horizonNode: HorizonNode as unknown as NodeTypes[string],
};

const edgeTypes: EdgeTypes = {
  nexusEdge: NexusFlowEdge as unknown as EdgeTypes[string],
};

// --- MiniMap node color ---

function getMiniMapNodeColor(node: Node): string {
  // Use hex values instead of CSS variables — MiniMap renders in SVG where CSS custom properties don't resolve
  if (node.type === 'phaseJunctionNode' || node.type === 'horizonNode') return '#ced4da';
  const entityData = node.data as NexusFlowNodeData;
  switch (entityData.nexusNode.visualState) {
    case NexusVisualState.Active: return '#339af0';
    case NexusVisualState.Done: return '#51cf66';
    case NexusVisualState.Cancelled: return '#fd7e14';
    case NexusVisualState.Muted: return '#ced4da';
    default: return '#ced4da';
  }
}

// --- Main Component ---

const NexusGraphInner: React.FC<NexusGraphProps> = ({data, isLoading, compact, onRefresh}) => {
  const canvasHeight = compact ? 'clamp(300px, 40vh, 500px)' : 'clamp(500px, 60vh, 800px)';
  const containerRef = useRef<HTMLDivElement>(null);

  const {nodes, edges} = useMemo(() => {
    if (!data || data.nodes.length === 0) return {nodes: [], edges: []};
    return graphToFlow(data);
  }, [data]);

  // Build adjacency map for hover highlighting (cheap, only recomputed when edges change)
  const adjacencyMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    for (const edge of edges) {
      if (!map.has(edge.source)) map.set(edge.source, new Set());
      if (!map.has(edge.target)) map.set(edge.target, new Set());
      map.get(edge.source)!.add(edge.target);
      map.get(edge.target)!.add(edge.source);
    }
    return map;
  }, [edges]);

  const handleNodeMouseEnter = useCallback((_: React.MouseEvent, node: Node) => {
    const container = containerRef.current;
    if (!container) return;
    container.setAttribute('data-highlighted-node', node.id);

    // Compute connected set
    const connected = adjacencyMap.get(node.id);
    const connectedIds = new Set<string>([node.id, ...(connected ?? [])]);

    // Mark connected nodes via DOM attribute (avoids React re-renders)
    container.querySelectorAll<HTMLElement>('.react-flow__node').forEach(el => {
      el.toggleAttribute('data-connected', connectedIds.has(el.dataset.id ?? ''));
    });

    // Mark connected edges — React Flow sets data-testid="rf__edge-{edgeId}" on edge groups
    const connectedEdgeIds = new Set<string>();
    edges.forEach(e => {
      if (e.source === node.id || e.target === node.id) connectedEdgeIds.add(e.id);
    });
    container.querySelectorAll<HTMLElement>('.react-flow__edge').forEach(el => {
      const testId = el.getAttribute('data-testid') ?? '';
      const edgeId = testId.replace('rf__edge-', '');
      el.toggleAttribute('data-connected', connectedEdgeIds.has(edgeId));
    });
  }, [adjacencyMap, edges]);

  const handleNodeMouseLeave = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    container.removeAttribute('data-highlighted-node');
    container.querySelectorAll('[data-connected]').forEach(el => {
      el.removeAttribute('data-connected');
    });
  }, []);

  if (isLoading) {
    return (
      <Box
        style={{
          height: canvasHeight,
          border: '1px solid var(--mantine-color-gray-3)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Stack align="center" gap="sm">
          <Loader size="md" />
          <Text size="sm" c="dimmed">
            Building graph...
          </Text>
        </Stack>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box
        style={{
          height: 300,
          border: '2px dashed var(--mantine-color-gray-3)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Stack align="center" gap="sm">
          <IconSearch size={32} color="var(--mantine-color-gray-4)" />
          <Text size="sm" c="dimmed">
            Search for an entity above to explore its workflow connections.
          </Text>
        </Stack>
      </Box>
    );
  }

  if (data.nodes.length === 0) {
    return (
      <Box
        style={{
          height: 300,
          border: '2px dashed var(--mantine-color-gray-3)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Stack align="center" gap="sm">
          <IconSearch size={32} color="var(--mantine-color-gray-4)" />
          <Text size="sm" c="dimmed">
            No workflow connections found for this entity.
          </Text>
        </Stack>
      </Box>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <StateLegend />
        {onRefresh && (
          <Tooltip label="Refresh graph" position="left">
            <ActionIcon variant="subtle" color="gray" size="sm" onClick={onRefresh} loading={isLoading}>
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
      <Box ref={containerRef} className="nexus-graph-container" style={{height: canvasHeight, border: '1px solid var(--mantine-color-gray-3)', borderRadius: 8}}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          panOnDrag
          zoomOnScroll
          proOptions={{hideAttribution: true}}
          onNodeMouseEnter={handleNodeMouseEnter}
          onNodeMouseLeave={handleNodeMouseLeave}>
          <Background variant={BackgroundVariant.Dots} gap={16} size={1.5} color="var(--mantine-color-gray-4)" />
          <Controls position="bottom-left" showInteractive={false} />
          <MiniMap
            position="bottom-right"
            pannable
            zoomable
            nodeColor={getMiniMapNodeColor}
          />
        </ReactFlow>
      </Box>
      {data.truncated && (
        <Alert color="orange" variant="light">
          Some connections are hidden because the graph reached its display limit.
        </Alert>
      )}
    </Stack>
  );
};

export const NexusGraph: React.FC<NexusGraphProps> = (props) => {
  return (
    <ReactFlowProvider>
      <NexusGraphInner {...props} />
    </ReactFlowProvider>
  );
};
